import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

const TABS = [
  { key: 'about',      label: 'About & History', icon: 'info'      },
  { key: 'contact',    label: 'Contact Info',    icon: 'contact_page' },
  { key: 'leadership', label: 'Leadership',      icon: 'groups'    },
];

export default function AdminContent() {
  const [tab,         setTab]         = useState('about');
  const [about,       setAbout]       = useState({ title: '', tagline: '', history: '', vision: '', mission: '', values: '' });
  const [contact,     setContact]     = useState({ email: '', phone: '', address: '', website: '' });
  const [leaders,     setLeaders]     = useState([]);
  const [saving,      setSaving]      = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [uploadingIdx,setUploadingIdx]= useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/content/about').catch(() => ({ data: { data: null } })),
      api.get('/content/contact').catch(() => ({ data: { data: null } })),
      api.get('/content/leadership').catch(() => ({ data: { data: [] } })),
    ]).then(([a, c, l]) => {
      if (a.data.data) setAbout(prev => ({ ...prev, ...a.data.data }));
      if (c.data.data) setContact(prev => ({ ...prev, ...c.data.data }));
      if (Array.isArray(l.data.data)) setLeaders(l.data.data);
    }).finally(() => setLoading(false));
  }, []);

  // ── About ────────────────────────────────────────────────────────────────
  const saveAbout = async () => {
    setSaving(true);
    try {
      await api.put('/content/about', about);
      toast.success('About content saved!');
    } catch { toast.error('Failed to save.'); }
    finally { setSaving(false); }
  };

  // ── Contact ──────────────────────────────────────────────────────────────
  const saveContact = async () => {
    setSaving(true);
    try {
      await api.put('/content/contact', contact);
      toast.success('Contact info saved!');
    } catch { toast.error('Failed to save.'); }
    finally { setSaving(false); }
  };

  // ── Leadership ───────────────────────────────────────────────────────────
  const addLeader = () =>
    setLeaders(ls => [...ls, { name: '', title: '', image: '', description: '', sortOrder: ls.length }]);

  const updateLeader = (i, field, value) =>
    setLeaders(ls => ls.map((l, idx) => idx === i ? { ...l, [field]: value } : l));

  const removeLeader = (i) => setLeaders(ls => ls.filter((_, idx) => idx !== i));

  const uploadLeaderImage = async (i, file) => {
    if (!file) return;
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await api.post('/content/upload-leader-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateLeader(i, 'image', r.data.url);
      toast.success('Image uploaded!');
    } catch { toast.error('Image upload failed.'); }
    finally { setUploadingIdx(null); }
  };

  const saveLeadership = async () => {
    setSaving(true);
    try {
      await api.put('/content/leadership', leaders);
      toast.success('Leadership updated!');
    } catch { toast.error('Failed to save.'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div>
      <h1 className="font-headline font-black text-3xl uppercase tracking-tight mb-8">Site Content</h1>
      <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 skeleton" />)}</div>
    </div>
  );

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-headline font-black text-3xl uppercase tracking-tight">Site Content</h1>
        <p className="font-body text-sm text-secondary mt-1">Edit public-facing content — about, contact, and leadership.</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-outline-variant/30 mb-8">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-5 py-3 font-headline font-bold text-sm uppercase tracking-tight border-b-2 -mb-px transition-colors ${
              tab === t.key ? 'border-primary-container text-primary-container' : 'border-transparent text-secondary hover:text-on-surface'
            }`}>
            <span className="material-symbols-outlined text-[18px]">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── About & History ─────────────────────────────────────────────── */}
      {tab === 'about' && (
        <div className="bg-white border border-outline-variant/20 p-8 space-y-6 max-w-3xl">
          <div>
            <label className="input-label">Page Title</label>
            <input value={about.title} onChange={e => setAbout(a => ({ ...a, title: e.target.value }))}
              className="input-field" placeholder="About Central Fasteners" />
          </div>
          <div>
            <label className="input-label">Tagline</label>
            <input value={about.tagline} onChange={e => setAbout(a => ({ ...a, tagline: e.target.value }))}
              className="input-field" placeholder="Short headline shown below the title" />
          </div>
          <div>
            <label className="input-label">Our Story / History</label>
            <p className="font-label text-xs text-secondary mb-1">Use line breaks to create paragraphs.</p>
            <textarea value={about.history} onChange={e => setAbout(a => ({ ...a, history: e.target.value }))}
              rows={8} className="input-field resize-none"
              placeholder="Write about the company history, founding story, milestones..." />
          </div>
          <div>
            <label className="input-label">Vision</label>
            <textarea value={about.vision} onChange={e => setAbout(a => ({ ...a, vision: e.target.value }))}
              rows={3} className="input-field resize-none" placeholder="Our vision statement..." />
          </div>
          <div>
            <label className="input-label">Mission</label>
            <textarea value={about.mission} onChange={e => setAbout(a => ({ ...a, mission: e.target.value }))}
              rows={3} className="input-field resize-none" placeholder="Our mission statement..." />
          </div>
          <div>
            <label className="input-label">Values</label>
            <textarea value={about.values} onChange={e => setAbout(a => ({ ...a, values: e.target.value }))}
              rows={3} className="input-field resize-none" placeholder="Core company values..." />
          </div>
          <button onClick={saveAbout} disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : <><span className="material-symbols-outlined">save</span> Save About Content</>}
          </button>
        </div>
      )}

      {/* ── Contact ─────────────────────────────────────────────────────── */}
      {tab === 'contact' && (
        <div className="bg-white border border-outline-variant/20 p-8 space-y-6 max-w-xl">
          <div>
            <label className="input-label">Email Address</label>
            <input value={contact.email} onChange={e => setContact(c => ({ ...c, email: e.target.value }))}
              className="input-field" placeholder="admin@centralfasteners.in" type="email" />
          </div>
          <div>
            <label className="input-label">Phone Number</label>
            <input value={contact.phone} onChange={e => setContact(c => ({ ...c, phone: e.target.value }))}
              className="input-field" placeholder="+91 XXXXX XXXXX" />
          </div>
          <div>
            <label className="input-label">Address</label>
            <textarea value={contact.address} onChange={e => setContact(c => ({ ...c, address: e.target.value }))}
              rows={3} className="input-field resize-none" placeholder="Full business address..." />
          </div>
          <div>
            <label className="input-label">Website</label>
            <input value={contact.website} onChange={e => setContact(c => ({ ...c, website: e.target.value }))}
              className="input-field" placeholder="centralfasteners.in" />
          </div>
          <button onClick={saveContact} disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : <><span className="material-symbols-outlined">save</span> Save Contact Info</>}
          </button>
        </div>
      )}

      {/* ── Leadership ──────────────────────────────────────────────────── */}
      {tab === 'leadership' && (
        <div className="space-y-6 max-w-3xl">
          <div className="flex items-center justify-between">
            <p className="font-body text-sm text-secondary">Add, edit or remove leadership team members.</p>
            <button onClick={addLeader} className="btn-primary">
              <span className="material-symbols-outlined">person_add</span> Add Member
            </button>
          </div>

          {leaders.length === 0 && (
            <div className="text-center py-16 border-2 border-dashed border-outline-variant/40">
              <span className="material-symbols-outlined text-[48px] text-outline-variant">groups</span>
              <p className="font-label text-xs uppercase tracking-widest text-secondary mt-3">No leadership members yet</p>
              <button onClick={addLeader} className="btn-ghost mt-2">
                <span className="material-symbols-outlined text-[16px]">add</span> Add First Member
              </button>
            </div>
          )}

          {leaders.map((leader, i) => (
            <div key={i} className="bg-white border border-outline-variant/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="font-headline font-black text-base uppercase tracking-tight">Member {i + 1}</span>
                <button onClick={() => removeLeader(i)} className="text-secondary hover:text-error transition-colors">
                  <span className="material-symbols-outlined text-[20px]">delete</span>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="input-label">Full Name *</label>
                  <input value={leader.name} onChange={e => updateLeader(i, 'name', e.target.value)}
                    className="input-field" placeholder="e.g. Rajesh Kumar" />
                </div>
                <div>
                  <label className="input-label">Title / Position *</label>
                  <input value={leader.title} onChange={e => updateLeader(i, 'title', e.target.value)}
                    className="input-field" placeholder="e.g. Chairman & CEO" />
                </div>
                <div>
                  <label className="input-label">Sort Order</label>
                  <input type="number" value={leader.sortOrder || 0}
                    onChange={e => updateLeader(i, 'sortOrder', parseInt(e.target.value) || 0)}
                    className="input-field" />
                </div>
                <div>
                  <label className="input-label">Photo</label>
                  <div className="flex gap-2 items-center">
                    <input type="file" accept="image/*"
                      onChange={e => uploadLeaderImage(i, e.target.files[0])}
                      className="input-field text-sm flex-1" />
                    {uploadingIdx === i && (
                      <span className="w-5 h-5 border-2 border-primary-container border-t-transparent rounded-full animate-spin flex-shrink-0" />
                    )}
                  </div>
                  {leader.image && (
                    <div className="mt-2 flex items-center gap-3">
                      <img
                        src={leader.image}
                        alt={leader.name}
                        className="w-16 h-16 object-cover object-top border border-outline-variant/20"
                      />
                      <button type="button" onClick={() => updateLeader(i, 'image', '')}
                        className="text-xs text-error hover:underline font-label">Remove</button>
                    </div>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="input-label">Description / Bio</label>
                  <textarea value={leader.description} onChange={e => updateLeader(i, 'description', e.target.value)}
                    rows={3} className="input-field resize-none"
                    placeholder="Short biography or role description..." />
                </div>
              </div>
            </div>
          ))}

          {leaders.length > 0 && (
            <button onClick={saveLeadership} disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : <><span className="material-symbols-outlined">save</span> Save Leadership Team</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
